import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/dashboard/Layout';
import RentalAlerts from '../components/RentalAlerts';
import { rentalsAPI, clientsAPI, assetsAPI, invoicesAPI } from '../services/api';
import { FileText, Plus, Eye, EyeOff, X, FileDown, Printer, UserPlus, Users, Download } from 'lucide-react';

const RentalsPageImproved = () => {
  const [rentals, setRentals] = useState([]);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAlerts, setShowAlerts] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatingDoc, setGeneratingDoc] = useState(false);
  
  // Client mode: 'existing' or 'new'
  const [clientMode, setClientMode] = useState('existing');
  
  // Rental form data
  const [formData, setFormData] = useState({
    // Client selection
    client_id: '',
    // New client data
    new_client: {
      full_name: '',
      email: '',
      phone: '',
      address: '',
      id_number: ''
    },
    // Rental data
    asset_ids: [],
    rental_type: 'daily',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    rate_per_unit: 0,
    total_amount: 0,
    delivery_address: '',
    notes: '',
    // BAST & Invoice data
    customer_name: '',
    invoice_number: '',
    guarantee_documents: '',
    dp_amount: 0,
    accessories: 'Mouse + Pad + Charger'
  });

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      const [rentalsRes, clientsRes, invoicesRes, assetsRes] = await Promise.all([
        rentalsAPI.getAll(),
        clientsAPI.getAll(),
        invoicesAPI.getAll(),
        assetsAPI.getAll({ status: 'ready' })
      ]);
      setRentals(rentalsRes.data);
      setClients(clientsRes.data);
      setInvoices(invoicesRes.data);
      setAssets(assetsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNewClientChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      new_client: { ...prev.new_client, [name]: value }
    }));
  };

  const handleAssetSelection = (assetId) => {
    setFormData(prev => {
      const isSelected = prev.asset_ids.includes(assetId);
      return {
        ...prev,
        asset_ids: isSelected 
          ? prev.asset_ids.filter(id => id !== assetId)
          : [...prev.asset_ids, assetId]
      };
    });
  };

  const calculateTotal = useCallback(() => {
    if (!formData.asset_ids.length || !formData.start_date || !formData.end_date) return;
    
    const start = new Date(formData.start_date);
    const end = new Date(formData.end_date);
    const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    
    if (daysDiff <= 0) return;
    
    let rate = 0;
    formData.asset_ids.forEach(assetId => {
      const asset = assets.find(a => a.id === assetId);
      if (asset) {
        if (formData.rental_type === 'daily') rate += asset.daily_rate || 0;
        else if (formData.rental_type === 'weekly') rate += asset.weekly_rate || 0;
        else if (formData.rental_type === 'monthly') rate += asset.monthly_rate || 0;
      }
    });

    let total = 0;
    if (formData.rental_type === 'daily') {
      total = rate * daysDiff;
    } else if (formData.rental_type === 'weekly') {
      total = rate * Math.ceil(daysDiff / 7);
    } else if (formData.rental_type === 'monthly') {
      total = rate * Math.ceil(daysDiff / 30);
    }

    setFormData(prev => ({ ...prev, rate_per_unit: rate, total_amount: total }));
  }, [formData.asset_ids, formData.rental_type, formData.start_date, formData.end_date, assets]);

  useEffect(() => {
    if (assets.length > 0) {
      calculateTotal();
    }
  }, [calculateTotal, assets]);

  // Auto-generate invoice number
  useEffect(() => {
    if (formData.start_date && !formData.invoice_number && rentals) {
      const date = new Date(formData.start_date);
      const invoiceNum = `INV/${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String((rentals?.length || 0) + 1).padStart(4, '0')}`;
      setFormData(prev => ({ ...prev, invoice_number: invoiceNum }));
    }
  }, [formData.start_date, formData.invoice_number, rentals]);

  const handleSubmit = async () => {
    try {
      setSaving(true);
      
      let clientId = formData.client_id;
      
      // Create new client if needed
      if (clientMode === 'new') {
        if (!formData.new_client.full_name || !formData.new_client.phone) {
          alert('Nama dan No. HP wajib diisi untuk client baru');
          return;
        }
        
        const clientPayload = {
          client_type: 'Individual',
          ...formData.new_client
        };
        
        try {
          const clientResponse = await clientsAPI.create(clientPayload);
          clientId = clientResponse.data.client_id || clientResponse.data.id;
          console.log('New client created:', clientId);
        } catch (clientError) {
          console.error('Failed to create client:', clientError);
          const errorMsg = clientError.response?.data?.detail || clientError.message || 'Unknown error';
          alert('Gagal membuat client baru: ' + (typeof errorMsg === 'object' ? JSON.stringify(errorMsg) : errorMsg));
          return;
        }
      }
      
      // Validate
      if (!clientId || !formData.asset_ids.length || !formData.start_date || !formData.end_date) {
        alert('Mohon lengkapi data rental (Client, Asset, Tanggal)');
        return;
      }

      // Create rental
      const rentalPayload = {
        client_id: clientId,
        asset_ids: formData.asset_ids,
        rental_type: formData.rental_type,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString(),
        rate_per_unit: formData.rate_per_unit,
        total_amount: formData.total_amount,
        delivery_address: formData.delivery_address || formData.new_client.address,
        notes: formData.notes
      };

      const response = await rentalsAPI.create(rentalPayload);
      const rentalId = response.data.rental_id || response.data.id;

      // Auto-generate BAST
      const customerName = clientMode === 'new' ? formData.new_client.full_name : clients.find(c => c.id === clientId)?.full_name || clients.find(c => c.id === clientId)?.company_name || '';
      
      if (formData.invoice_number && customerName) {
        const docData = {
          rental_id: rentalId,
          customer_name: customerName,
          invoice_number: formData.invoice_number,
          guarantee_documents: formData.guarantee_documents || 'KTP',
          dp_amount: formData.dp_amount,
          accessories: formData.accessories
        };
        
        try {
          await handleGenerateDocument(docData);
        } catch (docError) {
          console.error('Document generation failed, but rental created:', docError);
        }
      }

      // Auto-create invoice for daily rental
      if (formData.rental_type === 'daily') {
        try {
          const invoicePayload = {
            client_id: clientId,
            rental_id: rentalId,
            subtotal: formData.total_amount,
            tax_amount: formData.total_amount * 0.11,
            total_amount: formData.total_amount * 1.11,
            due_date: new Date(formData.end_date).toISOString(),
            items: formData.asset_ids.map(assetId => {
              const asset = assets.find(a => a.id === assetId);
              return {
                description: `Sewa ${asset?.brand} ${asset?.model}`,
                quantity: 1,
                unit_price: asset?.daily_rate || 0,
                amount: asset?.daily_rate || 0
              };
            })
          };
          
          await invoicesAPI.create(invoicePayload);
          console.log('Invoice auto-created');
        } catch (invError) {
          console.error('Invoice creation failed, but rental created:', invError);
        }
      }

      alert('✅ Rental berhasil dibuat!\n' + (formData.invoice_number ? '📄 BAST sudah di-generate\n' : '') + (formData.rental_type === 'daily' ? '💵 Invoice sudah dibuat' : ''));
      
      // Reload data
      await loadAllData();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Failed to create rental:', error);
      const errorDetail = error.response?.data?.detail || error.message || 'Unknown error';
      const errorMsg = typeof errorDetail === 'object' ? JSON.stringify(errorDetail, null, 2) : errorDetail;
      alert('❌ Gagal membuat rental:\n' + errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateDocument = async (docData) => {
    try {
      setGeneratingDoc(true);

      const response = await rentalsAPI.generateAgreement(docData);
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `BAST_${docData.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('BAST generated successfully');
    } catch (error) {
      console.error('Failed to generate BAST:', error);
      throw error;
    } finally {
      setGeneratingDoc(false);
    }
  };

  const resetForm = () => {
    setClientMode('existing');
    setFormData({
      client_id: '',
      new_client: {
        full_name: '',
        email: '',
        phone: '',
        address: '',
        id_number: ''
      },
      asset_ids: [],
      rental_type: 'daily',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      rate_per_unit: 0,
      total_amount: 0,
      delivery_address: '',
      notes: '',
      customer_name: '',
      invoice_number: '',
      guarantee_documents: '',
      dp_amount: 0,
      accessories: 'Mouse + Pad + Charger'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Rental Management</h1>
            <p className="text-muted-foreground">Manage laptop rentals and generate documents</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={async () => {
                try {
                  await rentalsAPI.exportCSV();
                  alert('✅ Export CSV berhasil!');
                } catch (error) {
                  alert('❌ Gagal export CSV: ' + error.message);
                }
              }}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
            >
              <FileDown className="w-4 h-4" />
              <span>Export CSV</span>
            </button>
            <button
              onClick={async () => {
                try {
                  await rentalsAPI.exportJSON();
                  alert('✅ Backup JSON berhasil!');
                } catch (error) {
                  alert('❌ Gagal backup: ' + error.message);
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <FileDown className="w-4 h-4" />
              <span>Backup JSON</span>
            </button>
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center space-x-2 btn"
              data-testid="add-rental-button"
            >
              <Plus className="w-4 h-4" />
              <span>New Rental</span>
            </button>
          </div>
        </div>

        {/* Alerts */}
        {showAlerts && (
          <RentalAlerts 
            rentals={rentals} 
            clients={clients} 
            invoices={invoices}
            onClose={() => setShowAlerts(false)} 
          />
        )}

        {/* Rentals List */}
        <div className="glass-card p-6 rounded-xl border border-border/40">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-foreground">Active Rentals</h2>
            <button
              onClick={() => setShowAlerts(!showAlerts)}
              className="text-muted-foreground hover:text-foreground"
            >
              {showAlerts ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          {rentals.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No rentals found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rentals.map((rental) => (
                <div key={rental.id} className="p-4 rounded-lg border border-border bg-background/50 hover:bg-muted/30 transition-colors">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-foreground">Rental #{rental.id?.slice(0, 8)}</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(rental.start_date).toLocaleDateString('id-ID')} - {new Date(rental.end_date).toLocaleDateString('id-ID')}
                      </p>
                    </div>
                    <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded">
                      {rental.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* New Rental Modal - IMPROVED */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="glass-card rounded-xl p-6 w-full max-w-4xl my-8 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-foreground">🚀 New Rental - Quick & Easy</h2>
                <button onClick={() => setShowModal(false)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* STEP 1: Client Selection */}
                <div className="p-4 bg-primary/5 border-2 border-primary/30 rounded-lg">
                  <h3 className="text-lg font-semibold text-foreground mb-4">👤 Step 1: Pilih/Buat Client</h3>
                  
                  <div className="flex space-x-2 mb-4">
                    <button
                      onClick={() => setClientMode('existing')}
                      className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                        clientMode === 'existing' 
                          ? 'bg-primary text-primary-foreground' 
                          : 'bg-muted text-foreground'
                      }`}
                    >
                      <Users className="w-4 h-4 inline mr-2" />
                      Client Lama
                    </button>
                    <button
                      onClick={() => setClientMode('new')}
                      className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                        clientMode === 'new' 
                          ? 'bg-primary text-primary-foreground' 
                          : 'bg-muted text-foreground'
                      }`}
                    >
                      <UserPlus className="w-4 h-4 inline mr-2" />
                      Client Baru
                    </button>
                  </div>

                  {clientMode === 'existing' ? (
                    <select
                      name="client_id"
                      value={formData.client_id}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-background border-2 border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground text-lg"
                      required
                    >
                      <option value="">-- Pilih Client --</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>
                          {client.full_name || client.company_name} | {client.phone}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Nama Lengkap *
                        </label>
                        <input
                          type="text"
                          name="full_name"
                          value={formData.new_client.full_name}
                          onChange={handleNewClientChange}
                          placeholder="Nama lengkap client"
                          className="w-full px-4 py-3 bg-background border-2 border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          No. HP *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.new_client.phone}
                          onChange={handleNewClientChange}
                          placeholder="08123456789"
                          className="w-full px-4 py-3 bg-background border-2 border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Email (opsional)
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.new_client.email}
                          onChange={handleNewClientChange}
                          placeholder="email@example.com"
                          className="w-full px-4 py-3 bg-background border-2 border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          No. KTP (opsional)
                        </label>
                        <input
                          type="text"
                          name="id_number"
                          value={formData.new_client.id_number}
                          onChange={handleNewClientChange}
                          placeholder="3174..."
                          className="w-full px-4 py-3 bg-background border-2 border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Alamat (opsional)
                        </label>
                        <input
                          type="text"
                          name="address"
                          value={formData.new_client.address}
                          onChange={handleNewClientChange}
                          placeholder="Alamat lengkap"
                          className="w-full px-4 py-3 bg-background border-2 border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* STEP 2: Asset Selection */}
                <div className="p-4 bg-secondary/5 border-2 border-secondary/30 rounded-lg">
                  <h3 className="text-lg font-semibold text-foreground mb-4">💻 Step 2: Pilih Laptop/Asset</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto border border-border rounded-lg p-3">
                    {assets.map(asset => (
                      <label key={asset.id} className="flex items-start space-x-3 cursor-pointer hover:bg-muted/50 p-3 rounded border-2 border-transparent hover:border-secondary/30">
                        <input
                          type="checkbox"
                          checked={formData.asset_ids.includes(asset.id)}
                          onChange={() => handleAssetSelection(asset.id)}
                          className="mt-1 w-5 h-5"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-foreground">
                            {asset.brand} {asset.model}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {asset.serial_number} | {asset.category}
                          </div>
                          <div className="text-xs font-medium text-primary mt-1">
                            Rp {(asset.daily_rate / 1000).toFixed(0)}K/hari
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    ✅ {formData.asset_ids.length} asset dipilih
                  </p>
                </div>

                {/* STEP 3: Rental Details */}
                <div className="p-4 bg-blue-500/5 border-2 border-blue-500/30 rounded-lg">
                  <h3 className="text-lg font-semibold text-foreground mb-4">📅 Step 3: Detail Rental</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Tipe Rental *</label>
                      <select
                        name="rental_type"
                        value={formData.rental_type}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-background border-2 border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                      >
                        <option value="daily">Harian</option>
                        <option value="weekly">Mingguan</option>
                        <option value="monthly">Bulanan</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Tanggal Mulai *</label>
                      <input
                        type="date"
                        name="start_date"
                        value={formData.start_date}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-background border-2 border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Tanggal Selesai *</label>
                      <input
                        type="date"
                        name="end_date"
                        value={formData.end_date}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-background border-2 border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                        required
                      />
                    </div>
                  </div>

                  {/* Total Calculation */}
                  <div className="mt-4 p-4 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-medium text-foreground">💰 Total Biaya:</span>
                      <span className="text-3xl font-bold text-primary">
                        Rp {formData.total_amount.toLocaleString('id-ID')}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Rate: Rp {formData.rate_per_unit.toLocaleString('id-ID')}/{formData.rental_type}
                    </p>
                  </div>
                </div>

                {/* STEP 4: BAST & Invoice Data */}
                <div className="p-4 bg-green-500/5 border-2 border-green-500/30 rounded-lg">
                  <h3 className="text-lg font-semibold text-foreground mb-4">📄 Step 4: Data BAST & Invoice (Auto)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Invoice Number (auto)</label>
                      <input
                        type="text"
                        name="invoice_number"
                        value={formData.invoice_number}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground"
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Dokumen Jaminan</label>
                      <input
                        type="text"
                        name="guarantee_documents"
                        value={formData.guarantee_documents}
                        onChange={handleInputChange}
                        placeholder="KTP & STNK"
                        className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">DP (Rp)</label>
                      <input
                        type="number"
                        name="dp_amount"
                        value={formData.dp_amount}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Accessories</label>
                      <input
                        type="text"
                        name="accessories"
                        value={formData.accessories}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground"
                      />
                    </div>
                  </div>
                  <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-sm text-blue-600 dark:text-blue-400">
                      <strong>✨ Auto-generate:</strong> BAST akan otomatis di-download setelah rental dibuat. Untuk rental harian, invoice juga otomatis dibuat.
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t-2 border-border">
                  <button
                    onClick={() => setShowModal(false)}
                    className="px-6 py-3 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
                    disabled={saving || generatingDoc}
                  >
                    Batal
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={saving || generatingDoc || !formData.asset_ids.length}
                    className="px-8 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 text-lg font-semibold"
                  >
                    {saving || generatingDoc ? (
                      <>
                        <span className="animate-spin">⏳</span>
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <FileDown className="w-5 h-5" />
                        <span>Buat Rental + BAST</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default RentalsPageImproved;
